import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { FileUp, X, Loader2, Sparkles, Plus } from "lucide-react";

const n = (v) => Number(v || 0);

export default function ImportPdfDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(null);

  const reset = () => { setFile(null); setDraft(null); setParsing(false); setCreating(false); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const parse = async () => {
    if (!file) { toast.error("Choose a PDF first"); return; }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = (await api.post("/work-orders/parse-pdf", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 })).data;
      setDraft(r);
      toast.success(`Read ${r.jobs.length} job(s) from the PDF — review below`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not parse that PDF");
    } finally {
      setParsing(false);
    }
  };

  const setCust = (k, v) => setDraft((d) => ({ ...d, customer: { ...d.customer, [k]: v } }));
  const setVeh = (k, v) => setDraft((d) => ({ ...d, vehicle: { ...d.vehicle, [k]: v } }));
  const setJob = (ji, patch) => setDraft((d) => ({ ...d, jobs: d.jobs.map((j, i) => (i === ji ? { ...j, ...patch } : j)) }));
  const rmJob = (ji) => setDraft((d) => ({ ...d, jobs: d.jobs.filter((_, i) => i !== ji) }));
  const setLine = (ji, key, li, patch) => setJob(ji, { [key]: draft.jobs[ji][key].map((l, i) => (i === li ? { ...l, ...patch } : l)) });
  const rmLine = (ji, key, li) => setJob(ji, { [key]: draft.jobs[ji][key].filter((_, i) => i !== li) });

  const laborTotal = (draft?.jobs || []).reduce((s, j) => s + j.labor_lines.reduce((a, l) => a + n(l.hours) * n(l.rate_charge), 0), 0);
  const partsTotal = (draft?.jobs || []).reduce((s, j) => s + j.parts_lines.reduce((a, l) => a + n(l.qty) * n(l.unit_price), 0), 0);
  const subtotal = laborTotal + partsTotal + n(draft?.shop_supply_fee);
  const hst = draft?.tax_applied ? subtotal * 0.13 : 0;

  const create = async () => {
    if (!draft.customer.name.trim()) { toast.error("Customer name is required"); return; }
    setCreating(true);
    try {
      const body = {
        customer: draft.customer,
        vehicle: { ...draft.vehicle, year: draft.vehicle.year ? Number(draft.vehicle.year) : null, mileage: draft.vehicle.mileage ? Number(draft.vehicle.mileage) : null },
        jobs: draft.jobs.map((j) => ({
          name: j.name,
          labor_lines: j.labor_lines.map((l) => ({ description: l.description, hours: n(l.hours), rate_cost: 0, rate_charge: n(l.rate_charge) })),
          parts_lines: j.parts_lines.map((p) => ({ name: p.name, part_number: p.part_number || "", qty: n(p.qty) || 1, unit_cost: 0, unit_price: n(p.unit_price) })),
        })),
        shop_supply_fee: n(draft.shop_supply_fee),
        tax_applied: !!draft.tax_applied,
        complaint: draft.complaint || "",
      };
      const r = (await api.post("/work-orders/import-pdf", body)).data;
      qc.invalidateQueries();
      toast.success(
        `Work order created — ${r.customer_name}${r.customer_created ? " (new customer)" : ""}, ${r.vehicle_label}${r.vehicle_created ? " (new vehicle)" : ""}`
      );
      close(false);
      navigate(`/mechanic/work-orders/${r.work_order.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create work order");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><FileUp className="h-5 w-5" />Import invoice PDF</DialogTitle>
          <DialogDescription>Upload an Identifix / Direct-Shop invoice — AI reads the customer, car and every job, then creates the work order. PDF prices go into Sell fields (your costs stay $0).</DialogDescription>
        </DialogHeader>

        {!draft && (
          <div className="space-y-3 py-2">
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg p-8 cursor-pointer hover:border-primary/60 transition-colors" data-testid="pdf-drop-zone">
              <FileUp className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm">{file ? file.name : "Click to choose the invoice PDF"}</span>
              <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="pdf-file-input" />
            </label>
            <Button className="w-full" onClick={parse} disabled={!file || parsing} data-testid="pdf-parse-btn">
              {parsing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reading PDF with AI…</>) : (<><Sparkles className="h-4 w-4 mr-2" />Read PDF</>)}
            </Button>
          </div>
        )}

        {draft && (
          <div className="space-y-4 py-2" data-testid="pdf-preview">
            {(draft.customer_match || draft.vehicle_match) && (
              <div className="text-xs rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 p-2" data-testid="pdf-match-banner">
                {draft.customer_match && <div>✓ Matches existing customer <b>{draft.customer_match.name}</b> — the job will attach to them.</div>}
                {draft.vehicle_match && <div>✓ Matches existing vehicle <b>{draft.vehicle_match.label}</b> (by VIN).</div>}
              </div>
            )}

            <div className="space-y-2">
              <Label className="font-semibold">Customer</Label>
              <div className="grid grid-cols-6 gap-2">
                <Input className="col-span-3" placeholder="Name *" value={draft.customer.name} onChange={(e) => setCust("name", e.target.value)} data-testid="pdf-cust-name" />
                <Input className="col-span-3" placeholder="Phone" value={draft.customer.phone} onChange={(e) => setCust("phone", e.target.value)} data-testid="pdf-cust-phone" />
                <Input className="col-span-3" placeholder="Email" value={draft.customer.email} onChange={(e) => setCust("email", e.target.value)} data-testid="pdf-cust-email" />
                <Input className="col-span-3" placeholder="Address" value={draft.customer.address} onChange={(e) => setCust("address", e.target.value)} />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-semibold">Vehicle</Label>
              <div className="grid grid-cols-6 gap-2">
                <Input className="col-span-1" placeholder="Year" value={draft.vehicle.year ?? ""} onChange={(e) => setVeh("year", e.target.value)} data-testid="pdf-veh-year" />
                <Input className="col-span-2" placeholder="Make" value={draft.vehicle.make} onChange={(e) => setVeh("make", e.target.value)} data-testid="pdf-veh-make" />
                <Input className="col-span-3" placeholder="Model" value={draft.vehicle.model} onChange={(e) => setVeh("model", e.target.value)} data-testid="pdf-veh-model" />
                <Input className="col-span-3" placeholder="VIN" value={draft.vehicle.vin} onChange={(e) => setVeh("vin", e.target.value.toUpperCase())} data-testid="pdf-veh-vin" />
                <Input className="col-span-1" placeholder="Plate" value={draft.vehicle.plate} onChange={(e) => setVeh("plate", e.target.value)} />
                <Input className="col-span-2" placeholder="Mileage" value={draft.vehicle.mileage ?? ""} onChange={(e) => setVeh("mileage", e.target.value)} />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="font-semibold">Jobs ({draft.jobs.length})</Label>
              {draft.jobs.map((j, ji) => (
                <div key={ji} className="rounded-md border border-border p-3 space-y-2 bg-secondary/10" data-testid={`pdf-job-${ji}`}>
                  <div className="flex items-center gap-2">
                    <Input className="h-8 font-medium" value={j.name} onChange={(e) => setJob(ji, { name: e.target.value })} data-testid={`pdf-job-name-${ji}`} />
                    <Button variant="ghost" size="icon" onClick={() => rmJob(ji)} data-testid={`pdf-job-remove-${ji}`}><X className="h-4 w-4" /></Button>
                  </div>
                  {j.labor_lines.map((l, li) => (
                    <div key={`l${li}`} className="grid gap-2 items-center [grid-template-columns:minmax(0,4fr)_70px_100px_70px_32px] text-sm">
                      <Input className="h-8" value={l.description} onChange={(e) => setLine(ji, "labor_lines", li, { description: e.target.value })} placeholder="Labor description" />
                      <Input className="h-8" type="number" step="0.25" value={l.hours} onChange={(e) => setLine(ji, "labor_lines", li, { hours: e.target.value })} title="Hours" />
                      <Input className="h-8" type="number" step="0.01" value={l.rate_charge} onChange={(e) => setLine(ji, "labor_lines", li, { rate_charge: e.target.value })} title="Rate $" />
                      <span className="text-xs font-mono-data text-right">{money(n(l.hours) * n(l.rate_charge))}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => rmLine(ji, "labor_lines", li)}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                  {j.parts_lines.map((p, li) => (
                    <div key={`p${li}`} className="grid gap-2 items-center [grid-template-columns:minmax(0,3fr)_minmax(0,1.5fr)_70px_100px_70px_32px] text-sm">
                      <Input className="h-8" value={p.name} onChange={(e) => setLine(ji, "parts_lines", li, { name: e.target.value })} placeholder="Part name" />
                      <Input className="h-8" value={p.part_number} onChange={(e) => setLine(ji, "parts_lines", li, { part_number: e.target.value })} placeholder="Part #" />
                      <Input className="h-8" type="number" value={p.qty} onChange={(e) => setLine(ji, "parts_lines", li, { qty: e.target.value })} title="Qty" />
                      <Input className="h-8" type="number" step="0.01" value={p.unit_price} onChange={(e) => setLine(ji, "parts_lines", li, { unit_price: e.target.value })} title="Sell $" />
                      <span className="text-xs font-mono-data text-right">{money(n(p.qty) * n(p.unit_price))}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => rmLine(ji, "parts_lines", li)}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-1">
                <Label className="label-mono">Shop supplies fee ($)</Label>
                <Input type="number" step="0.01" value={draft.shop_supply_fee} onChange={(e) => setDraft((d) => ({ ...d, shop_supply_fee: e.target.value }))} data-testid="pdf-supply-fee" />
              </div>
              <label className="flex items-center gap-2 text-sm pb-2">
                <input type="checkbox" checked={!!draft.tax_applied} onChange={(e) => setDraft((d) => ({ ...d, tax_applied: e.target.checked }))} data-testid="pdf-tax-toggle" />
                Charge HST (13%)
              </label>
            </div>

            <div className="border-t border-border pt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm font-mono-data" data-testid="pdf-totals">
              <span>Labor: <b>{money(laborTotal)}</b></span>
              <span>Parts: <b>{money(partsTotal)}</b></span>
              <span>HST: <b>{money(hst)}</b></span>
              <span>Total: <b>{money(subtotal + hst)}</b></span>
            </div>
          </div>
        )}

        <DialogFooter>
          {draft && <Button variant="outline" onClick={reset} disabled={creating}>Start over</Button>}
          <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
          {draft && (
            <Button onClick={create} disabled={creating} data-testid="pdf-create-wo-btn">
              {creating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</>) : (<><Plus className="h-4 w-4 mr-2" />Create Work Order</>)}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
